import { httpClient } from '@bs/core'

// POS-agnostic Customer endpoints under the shared `/pantry/stores/:storeId`
// prefix (same convention as `/pantry/stores/:storeId/category-mapping` in
// `categoryApis.ts`). Unlike `covaCustomerApis.ts` / `greenlineCustomerApis.ts`,
// calls here are identical across store types, so they live in their own file
// rather than being duplicated per store.

const resetPasswordUrl = (storeId: string, wooCustomerId: string) =>
  `/pantry/stores/${storeId}/customers/${wooCustomerId}/forgot-password`

// Sends a reset-password email to the customer's storefront (WooCommerce)
// account. `wooCustomerId` — NOT the POS customerId — is the path segment the
// backend expects; it addresses the woo account the email gets sent to.
// Confirmed live in production (ACD-15023): no request body needed, and the
// response is always the generic `{ message: "If an account with that email
// exists, a reset link has been sent" }` regardless of whether the account
// exists, so the UI must not infer anything from the response beyond success.
export const resetCustomerPassword = async (
  storeId: string,
  wooCustomerId: string,
): Promise<void> => {
  await httpClient.json.post(resetPasswordUrl(storeId, wooCustomerId), {})
}

// Translates the Customer list "All Spend" bucket (a UI-only enum) into the
// `clv_from`/`clv_to` range params both the Cova and Greenline list endpoints
// actually understand. Both `GetCustomersSerializer` (cova) and
// `PaginateSerializer` (greenline) accept these as plain floats — see
// `backend/be_api/crm_be_cova_api/serializers/v2/customers.py` and
// `backend/be_api/crm_be_v2_api/serializers/greenline/customers.py`.
// Boundaries are inclusive on both ends to match the backend's `gte`/`lte`
// range filter (a customer at exactly $100 CLV can appear in either the
// "Under $100" or "$100 – $500" bucket depending on rounding — acceptable,
// matches the mockup's own bucket boundaries).
export const spendBucketToClvRange = (
  bucket: 'lt100' | '100_500' | '500_1k' | 'gt1k',
): { clv_from?: number; clv_to?: number } => {
  switch (bucket) {
    case 'lt100':
      return { clv_to: 100 }
    case '100_500':
      return { clv_from: 100, clv_to: 500 }
    case '500_1k':
      return { clv_from: 500, clv_to: 1000 }
    case 'gt1k':
      return { clv_from: 1000 }
    default:
      return {}
  }
}
